import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationRef,
  DoBootstrap,
  NgModule,
} from '@angular/core';
import {
  MAT_RIPPLE_GLOBAL_OPTIONS,
  RippleGlobalOptions,
} from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UpgradeModule } from '@angular/upgrade/static';
import { UIRouterUpgradeModule } from '@uirouter/angular-hybrid';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { setTheme } from 'ngx-bootstrap/utils';
import 'src/app/ajs-downgraded-providers';
import {
  $lazyLoadProvider,
  analyticsFactoryProvider,
  bigQueryLoggingProvider,
  canAccessAppsProvider,
  companyAssetsServiceProvider,
  exceptionHandlerProvider,
  helpWidgetFactoryProvider,
  loadingServiceProvider,
  processErrorCodeProvider,
  scrollingListServiceProvider,
  urlStateServiceProvider,
} from './ajs-upgraded-providers';
import { provideStoreApiService } from './api/infra/store-api-service.injector';
import { AppCommonModule } from './app-common/app-common.module';
import { AppComponent } from './app-common/components/app/app.component';
import { AuthModule } from './auth/auth.module';
import { BillingModule } from './billing/billing.module';
import { ComponentsModule } from './components/components.module';
import { DisplaysModule } from './displays/displays.module';
import { EditorModule } from './editor/editor.module';
import { PurchaseModule } from './purchase/purchase.module';
import { SchedulesModule } from './schedules/schedules.module';
import { ScreenSharingModule } from './screen-sharing/screen-sharing.module';
import { SettingsModule } from './settings/settings.module';
import { TimepickerTabIndexDirective } from './shared/directives/timepicker-tab-index.directive';
import { IconService } from './shared/services/icon.service';
import { ScopedMatIconRegistry } from './shared/services/scoped-mat-icon-registry';
import { SharedModule } from './shared/shared.module';
import { SitemapModule } from './sitemap/sitemap.module';
import { StorageModule } from './storage/storage.module';
import { TemplateEditorModule } from './template-editor/template-editor.module';

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 0,
    exitDuration: 0,
  },
};

@NgModule({
  declarations: [],
  bootstrap: [],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    UpgradeModule,
    ModalModule.forRoot(),
    TooltipModule.forRoot(),
    PopoverModule.forRoot(),
    TimepickerModule.forRoot(),
    TimepickerTabIndexDirective,
    ProgressbarModule.forRoot(),
    UIRouterUpgradeModule.forRoot(),
    SharedModule,
    AppCommonModule,
    ComponentsModule,
    SettingsModule,
    BillingModule,
    EditorModule,
    DisplaysModule,
    PurchaseModule,
    TemplateEditorModule,
    StorageModule,
    AuthModule,
    SchedulesModule,
    ScreenSharingModule,
    SitemapModule,
  ],
  providers: [
    $lazyLoadProvider,
    loadingServiceProvider,
    exceptionHandlerProvider,
    analyticsFactoryProvider,
    bigQueryLoggingProvider,
    canAccessAppsProvider,
    helpWidgetFactoryProvider,
    processErrorCodeProvider,
    scrollingListServiceProvider,
    urlStateServiceProvider,
    companyAssetsServiceProvider,
    provideStoreApiService(),
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    { provide: MatIconRegistry, useClass: ScopedMatIconRegistry },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: APP_INITIALIZER,
      useFactory: (iconService: IconService) => {
        iconService.registerIcons();
      },
      deps: [IconService],
    },
  ],
})
export class AppModule implements DoBootstrap {
  constructor(private upgrade: UpgradeModule) {
    setTheme('bs4');
  }

  ngDoBootstrap(appRef: ApplicationRef): void {
    console.log('Bootstraping AngularJS');
    this.upgrade.bootstrap(document.documentElement, ['risevision.apps']);
    appRef.bootstrap(AppComponent);
  }
}
